import * as React from 'react'
import { useTranslation } from 'react-i18next'

type UserContactInputErrors = {
  email: string
  lastName: string
  firstName: string
  [key: string]: string
}

type UserContactValues = {
  firstName: string
  lastName: string
  phone: string
  responsibilityDescription: string
  receiveRequestForProposals: boolean
}

type UserContactInitForm = {
  email: string
  firstName: string
  lastName: string
  phone: string
  responsibilityDescription: string
  receiveRequestForProposals: boolean
}

type UseVendorContactFormReturnType = {
  vendorContactFormProps: {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    values: UserContactValues
    email: string
    onEmailChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onReceiveRequestForProposalsChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
    formErrors: UserContactInputErrors
  }
  vendorContactValues: UserContactValues
  email: string
  initForm: (data: UserContactInitForm) => void
}

export const useUserContactForm = (contacts?: VendorContactsResponseType): UseVendorContactFormReturnType => {
  const [email, setEmail] = React.useState('')
  const { t } = useTranslation()

  const [vendorContactValues, setVendorContactValues] = React.useState({
    firstName: '',
    lastName: '',
    phone: '',
    responsibilityDescription: '',
    receiveRequestForProposals: false
  })

  const [formErrors, setFormErrors] = React.useState<UserContactInputErrors>({
    email: '',
    lastName: '',
    firstName: ''
  })

  const handleVendorContactChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVendorContactValues((oldValues) => ({ ...oldValues, [event.target.name]: event.target.value }))
  }

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  const handleSetReceiveRequestForProposals = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVendorContactValues((oldValues) => ({
      ...oldValues,
      receiveRequestForProposals: event.target.checked
    }))
  }

  const handleBlurValidation = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { value, name } = event.target
      const errorValue =
        value === ''
          ? t('required-field')
          : contacts?.generalContacts.some((contact) => contact.email.toLowerCase() === value.toLowerCase()) ||
            contacts?.userContacts.some((contact) => contact.email.toLowerCase() === value.toLowerCase())
          ? t('this-email-address-is-already-in-contacts')
          : ''

      setFormErrors((oldState) => ({ ...oldState, [name]: errorValue }))
    },
    [contacts, t]
  )

  const initForm = React.useCallback((data: UserContactInitForm) => {
    const { email, ...rest } = data
    setVendorContactValues(rest)
    setEmail(email)
  }, [])

  return {
    vendorContactFormProps: {
      onChange: handleVendorContactChange,
      values: vendorContactValues,
      email: email,
      onEmailChange: handleEmailChange,
      onReceiveRequestForProposalsChange: handleSetReceiveRequestForProposals,
      onBlur: handleBlurValidation,
      formErrors: formErrors
    },
    vendorContactValues,
    email,
    initForm
  }
}
